import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import ChoosePlanGate from '../components/ChoosePlanGate';
import ChoosePlanModal from '../components/ChoosePlanModal';
import './AnalyticsDashboard.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface DashboardStats {
  totalProjects: number;
  totalVideos: number;
  totalShorts: number;
  activeJobs: number;
  completedThisWeek: number;
  shortsThisWeek: number;
  lastWeekShorts: number;
  avgShortsPerVideo: number;
  successRate: number;
}

interface ProcessingJob {
  jobId: string;
  videoId: string;
  fileName: string;
  status: string;
  stage: string;
  progress: number;
  createdAt: string;
  projectId: string;
  projectName: string;
}

interface TopShort {
  id: string;
  title: string;
  videoId: string;
  projectId: string;
  projectName: string;
  duration: number;
  createdAt: string;
  platform: string;
}

interface RecentStory {
  id: string;
  name: string;
  projectId: string | null;
  projectName: string;
  status: string;
  slideCount: number;
  updatedAt: string;
}

interface RecentCaption {
  id: string;
  name: string;
  projectId: string | null;
  projectName: string;
  status: string;
  updatedAt: string;
}

interface DashboardData {
  stats: DashboardStats;
  processingQueue: ProcessingJob[];
  topShorts: TopShort[];
  recentStories: RecentStory[];
  recentCaptions: RecentCaption[];
}

interface AnalyticsSummary {
  totalViews: number;
  totalEngagement: number;
  totalWatchMinutes: number;
  activeAccounts: number;
}

interface PlatformStat {
  platform: string;
  accountCount: number;
  viewsSum: number;
}

interface PricingEffectiveEntitlement {
  max: number | null;
  source: string;
}

interface PricingAssignedPlan {
  id: string;
  name: string;
  priceMonthly: number;
  status: 'active' | 'archived';
}

interface PricingState {
  mode: 'payg' | 'subscription';
  assignedPlan: PricingAssignedPlan | null;
  effective: Record<string, PricingEffectiveEntitlement>;
  usage: {
    currentPeriod: Record<string, number>;
  };
}

const STALE_JOB_HOURS = 120;

const CREDIT_FEATURES: { key: string; label: string }[] = [
  { key: 'shorts', label: 'Shorts' },
  { key: 'slideshow', label: 'Slideshow' },
  { key: 'subtitle', label: 'Subtitles' },
  { key: 'youtube_posts', label: 'YouTube Posts' },
  { key: 'kinetic_caption', label: 'Kinetic Captions' },
  { key: 'y_ray', label: 'Y-Ray' },
];

const DASHBOARD_TIPS = [
  'Use Kinetic Caption to add word-by-word animated captions to any short.',
  'Turn one long video into multiple shorts automatically with Video to Shorts.',
  'Use a GIF as your video background for more eye-catching shorts.',
  'Fine-tune cuts and caption timing for any clip in the Clip Editor before publishing.',
  'Build a Story from images or clips and turn it into a short in one flow.',
];

function getSessionTip(): string {
  try {
    const stored = sessionStorage.getItem('dashboard_tip');
    if (stored) return stored;
    const tip = DASHBOARD_TIPS[Math.floor(Math.random() * DASHBOARD_TIPS.length)];
    sessionStorage.setItem('dashboard_tip', tip);
    return tip;
  } catch {
    return DASHBOARD_TIPS[0];
  }
}

const Dashboard: React.FC<{ choosePlan?: boolean }> = ({ choosePlan = false }) => {
  const { user } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyticsSummary, setAnalyticsSummary] = useState<AnalyticsSummary | null>(null);
  const [platformStats, setPlatformStats] = useState<PlatformStat[]>([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [pricingState, setPricingState] = useState<PricingState | null>(null);
  const [tip] = useState<string>(() => getSessionTip());
  const leftColumnRef = useRef<HTMLDivElement>(null);
  const [leftColumnHeight, setLeftColumnHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/me/pricing`, { credentials: 'include' });
        if (res.ok) {
          const pricingData = await res.json();
          setPricingState(pricingData);
        }
      } catch (err) {
        console.error('Failed to fetch pricing state:', err);
      }
    };

    fetchPricing();
  }, []);

  useEffect(() => {
    const el = leftColumnRef.current;
    if (!el) return;
    const updateHeight = () => setLeftColumnHeight(el.offsetHeight);
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading]);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/dashboard`, { credentials: 'include' });
        if (res.ok) {
          const dashboardData = await res.json();
          setData(dashboardData);
        }
      } catch (err) {
        console.error('Failed to fetch dashboard:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
    const interval = setInterval(fetchDashboard, 5000); // Refresh every 5s
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchAnalyticsOverview = async () => {
      try {
        const [overviewRes, platformsRes] = await Promise.all([
          fetch(`${API_BASE}/api/analytics/dashboard/overview?rangeKey=lifetime`, { credentials: 'include' }),
          fetch(`${API_BASE}/api/analytics/dashboard/platforms/compare?rangeKey=lifetime`, { credentials: 'include' }),
        ]);
        const overviewJson = await overviewRes.json().catch(() => ({ success: false }));
        const platformsJson = await platformsRes.json().catch(() => ({ success: false }));

        if (overviewRes.ok && overviewJson.success) {
          const summary = overviewJson.data?.summary || {};
          setAnalyticsSummary({
            totalViews: summary.totalViews || 0,
            totalEngagement: summary.totalEngagement || 0,
            totalWatchMinutes: summary.totalWatchMinutes || 0,
            activeAccounts: summary.activeAccounts || 0,
          });
        }
        if (platformsRes.ok && platformsJson.success) {
          setPlatformStats(platformsJson.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch analytics overview:', err);
      } finally {
        setAnalyticsLoading(false);
      }
    };

    fetchAnalyticsOverview();
  }, []);

  const getTrend = (current: number, previous: number): { percent: number; isUp: boolean } => {
    if (previous === 0) return { percent: current > 0 ? 100 : 0, isUp: current > 0 };
    const percent = ((current - previous) / previous) * 100;
    return { percent: Math.abs(percent), isUp: percent >= 0 };
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getPlatformIcon = (platform: string): string => {
    switch (platform) {
      case 'youtube_shorts': return '📺';
      case 'tiktok': return '🎵';
      case 'instagram_reels': return '📸';
      default: return '🎬';
    }
  };

  const formatPlatformName = (platform: string): string => {
    if (platform === 'youtube') return 'YouTube';
    if (platform === 'tiktok') return 'TikTok';
    if (platform === 'linkedin') return 'LinkedIn';
    if (platform === 'twitter') return 'X';
    if (!platform) return 'Unknown';
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  };

  const formatCompact = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'var(--accent-text)';
      case 'failed': return 'var(--status-error-text)';
      case 'queued': return 'var(--status-neutral)';
      default: return 'var(--status-info-text)';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'transcribing': return 'Transcribing';
      case 'analyzing': return 'Analyzing';
      case 'segmenting': return 'Segmenting';
      case 'clipping': return 'Clipping';
      case 'completed': return 'Completed';
      case 'failed': return 'Failed';
      case 'queued': return 'Queued';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <main className="main-content">
          <Header />
          <div className="dashboard-loading">Loading dashboard...</div>
        </main>
        {choosePlan ? <ChoosePlanModal /> : <ChoosePlanGate />}
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="main-content">
        <Header />

        {/* Welcome Header */}
        <div className="dashboard-welcome">
          <div>
            <h1>Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}! 👋</h1>
            <p className="dashboard-subtitle">{tip}</p>
          </div>
          <div className="dashboard-plan-badge">
            {pricingState?.assignedPlan ? (
              <>
                <span className="dashboard-plan-name">{pricingState.assignedPlan.name} Plan</span>
                <span className="dashboard-plan-status">{pricingState.assignedPlan.status}</span>
              </>
            ) : (
              <>
                <span className="dashboard-plan-name">Free Plan</span>
                <Link to="/account/billing" className="btn-cta">Upgrade</Link>
              </>
            )}
          </div>
        </div>

        <div className="dashboard-top-row">
        <div className="dashboard-top-main" ref={leftColumnRef}>

        {/* Stats Cards */}
        <div className="dashboard-section-block">
          <h3>GENERAL</h3>
          <div className="kpi-tiles-grid">
          <article className="analytics-kpi-card">
            <div className="analytics-kpi-label">Total Projects</div>
            <div className="analytics-kpi-value">{data?.stats.totalProjects || 0}</div>
            <div className="analytics-kpi-meta">
              {data?.stats.totalVideos
                ? `${(data.stats.totalVideos / (data.stats.totalProjects || 1)).toFixed(1)} videos/project`
                : 'No videos yet'}
            </div>
          </article>

          <article className="analytics-kpi-card">
            <div className="analytics-kpi-label">Total Shorts</div>
            <div className="analytics-kpi-value">{data?.stats.totalShorts || 0}</div>
            <div className="analytics-kpi-meta">
              {data?.stats.shortsThisWeek !== undefined && data?.stats.lastWeekShorts !== undefined ? (
                (() => {
                  const trend = getTrend(data.stats.shortsThisWeek, data.stats.lastWeekShorts);
                  return (
                    <>
                      <span className={trend.isUp ? 'trend-up' : 'trend-down'}>
                        {trend.isUp ? '↗' : '↘'} {trend.percent.toFixed(0)}%
                      </span>{' '}
                      vs last week
                    </>
                  );
                })()
              ) : 'No comparison data'}
            </div>
          </article>

          <article className="analytics-kpi-card">
            <div className="analytics-kpi-label">Avg Shorts/Video</div>
            <div className="analytics-kpi-value">{data?.stats.avgShortsPerVideo?.toFixed(1) || '0.0'}</div>
            <div className="analytics-kpi-meta">
              Success rate: {data?.stats.successRate?.toFixed(0) || 100}%
            </div>
          </article>

          <article className="analytics-kpi-card">
            <div className="analytics-kpi-label">Processing Now</div>
            <div className="analytics-kpi-value">{data?.stats.activeJobs || 0}</div>
            <div className="analytics-kpi-meta">
              {data?.stats.completedThisWeek
                ? `${data.stats.completedThisWeek} completed this week`
                : 'No jobs completed yet'}
            </div>
          </article>
          </div>
        </div>

        {/* Connected Accounts */}
        <div className="dashboard-section-block">
          <h3>ACCOUNTS</h3>
          {platformStats.length > 0 ? (
            <div className="kpi-tiles-grid">
              {platformStats.map((platform) => (
                <article className="analytics-kpi-card" key={platform.platform}>
                  <div className="analytics-kpi-label">{formatPlatformName(platform.platform)}</div>
                  <div className="analytics-kpi-value">{platform.accountCount}</div>
                  <div className="analytics-kpi-meta">{formatCompact(platform.viewsSum)} views</div>
                </article>
              ))}
            </div>
          ) : !analyticsLoading ? (
            <div className="empty-state">
              <p>No social accounts connected yet</p>
              <Link to="/account" className="btn-cta">Connect an account</Link>
            </div>
          ) : null}
        </div>

        {/* Performance */}
        {analyticsSummary && (
          <div className="dashboard-section-block">
            <h3>PERFORMANCE</h3>
            <div className="kpi-tiles-grid">
              <article className="analytics-kpi-card">
                <div className="analytics-kpi-label">Total Views</div>
                <div className="analytics-kpi-value">{formatCompact(analyticsSummary.totalViews)}</div>
                <div className="analytics-kpi-meta">All-time across accounts</div>
              </article>

              <article className="analytics-kpi-card">
                <div className="analytics-kpi-label">Engagement</div>
                <div className="analytics-kpi-value">{formatCompact(analyticsSummary.totalEngagement)}</div>
                <div className="analytics-kpi-meta">All-time across accounts</div>
              </article>

              <article className="analytics-kpi-card">
                <div className="analytics-kpi-label">Watch Time</div>
                <div className="analytics-kpi-value">{formatCompact(analyticsSummary.totalWatchMinutes)}m</div>
                <div className="analytics-kpi-meta">All-time across accounts</div>
              </article>
            </div>
          </div>
        )}

        </div>

        {/* Notice Board */}
        <aside
          className="dashboard-announcement dashboard-section-block"
          style={leftColumnHeight ? { height: leftColumnHeight } : undefined}
        >
          <h3>ANNOUNCEMENT</h3>
          <div className="notice-board-list">
            <article className="analytics-kpi-card notice-tile-corners">
              <div className="analytics-kpi-label">Founding Member Plan</div>
              <Link to="/account/billing" className="btn-cta notice-tile-action">Subscribe</Link>
            </article>

            <article className="analytics-kpi-card">
              <div className="analytics-kpi-label">Story</div>
              <div className="analytics-kpi-meta">
                Fixed memory issues. <Link to="/projects" className="view-all-link">View →</Link>
              </div>
            </article>

            <article className="analytics-kpi-card">
              <div className="analytics-kpi-label">Clip Editor</div>
              <Link to="/projects" className="view-all-link">View →</Link>
            </article>

            <article
              className="analytics-kpi-card notice-tile-media notice-tile-corners"
              style={{ backgroundImage: "linear-gradient(180deg, rgba(0,0,0,0.15), rgba(0,0,0,0.65)), url('/gif/kinetic-captions-sidebyside.gif')" }}
            >
              <div className="analytics-kpi-label">Kinetic Caption</div>
              <Link to="/projects" className="view-all-link notice-tile-action">View →</Link>
            </article>

            <article
              className="analytics-kpi-card notice-tile-media notice-tile-corners"
              style={{ backgroundImage: "linear-gradient(180deg, rgba(0,0,0,0.15), rgba(0,0,0,0.65)), url('/gif/video-to-shorts-banner.gif')" }}
            >
              <div className="analytics-kpi-label">Video to Shorts</div>
              <Link to="/projects" className="view-all-link notice-tile-action">View →</Link>
            </article>
          </div>
        </aside>

        </div>

        {/* Credits */}
        <div className="dashboard-section-block">
          <h3>CREDITS</h3>
          <div className="kpi-tiles-grid">
            {CREDIT_FEATURES.map((feature) => {
              const entitlement = pricingState?.effective[feature.key];
              const max = entitlement?.max ?? null;
              const used = pricingState?.usage.currentPeriod[feature.key] ?? 0;
              const remaining = max === null ? null : Math.max(max - used, 0);
              return (
                <article className="analytics-kpi-card" key={feature.key}>
                  <div className="analytics-kpi-label">{feature.label}</div>
                  <div className="analytics-kpi-value">{remaining === null ? '∞' : remaining}</div>
                  <div className="analytics-kpi-meta">
                    {max === null ? 'Unlimited' : `${used}/${max} used`}
                  </div>
                </article>
              );
            })}
          </div>
        </div>

        {/* Recents */}
        <div className="dashboard-section-block">
          <h3>RECENTS</h3>
          <div className="recents-row">
            {/* Recent Shorts */}
            <div className="dashboard-section">
              <div className="dashboard-section-header">
                <h3>Shorts</h3>
                <Link to="/clips" className="view-all-link">View All →</Link>
              </div>
              <div className="recent-item-list">
                {data?.topShorts && data.topShorts.length > 0 ? (
                  data.topShorts.map((short) => (
                    <Link key={short.id} to={`/clips/${short.id}`} className="recent-item-card">
                      <div className="recent-item-icon">{getPlatformIcon(short.platform)}</div>
                      <div className="recent-item-info">
                        <div className="recent-item-title">{short.title}</div>
                        <div className="recent-item-meta">
                          <span className="recent-item-tag">{short.projectName}</span>
                          <span>{formatDuration(short.duration)}</span>
                        </div>
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="empty-state">
                    <p>No shorts yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Stories */}
            <div className="dashboard-section">
              <div className="dashboard-section-header">
                <h3>Stories</h3>
              </div>
              <div className="recent-item-list">
                {data?.recentStories && data.recentStories.length > 0 ? (
                  data.recentStories.map((story) => (
                    <Link
                      key={story.id}
                      to={story.projectId ? `/projects/${story.projectId}/image-video/${story.id}` : '/projects'}
                      className="recent-item-card"
                    >
                      <div className="recent-item-icon">📖</div>
                      <div className="recent-item-info">
                        <div className="recent-item-title">{story.name}</div>
                        <div className="recent-item-meta">
                          <span className="recent-item-tag">{story.projectName}</span>
                          <span>{story.slideCount} slides</span>
                        </div>
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="empty-state">
                    <p>No stories yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Captions */}
            <div className="dashboard-section">
              <div className="dashboard-section-header">
                <h3>Captions</h3>
              </div>
              <div className="recent-item-list">
                {data?.recentCaptions && data.recentCaptions.length > 0 ? (
                  data.recentCaptions.map((caption) => (
                    <Link
                      key={caption.id}
                      to={caption.projectId ? `/projects/${caption.projectId}/kinetic-caption/${caption.id}` : '/projects'}
                      className="recent-item-card"
                    >
                      <div className="recent-item-icon">💬</div>
                      <div className="recent-item-info">
                        <div className="recent-item-title">{caption.name}</div>
                        <div className="recent-item-meta">
                          <span className="recent-item-tag">{caption.projectName}</span>
                          <span>{getStatusLabel(caption.status)}</span>
                        </div>
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="empty-state">
                    <p>No captions yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Active Processing Queue */}
        {data?.processingQueue && data.processingQueue.length > 0 && (
          <div className="dashboard-section-block">
            <h3>⚡ ACTIVE PROCESSING QUEUE ({data.processingQueue.length})</h3>
            <div className="dashboard-section">
              <div className="recents-row">
                {data.processingQueue.map((job) => {
                  const isStale = Date.now() - new Date(job.createdAt).getTime() > STALE_JOB_HOURS * 3600 * 1000;
                  const effectiveStatus = isStale ? 'failed' : job.status;
                  return (
                    <div key={job.jobId} className="recent-item-card">
                      <div className="recent-item-icon">⚡</div>
                      <div className="recent-item-info">
                        <div className="recent-item-title">{job.fileName}</div>
                        <div className="recent-item-meta">
                          <span className="recent-item-tag">{job.projectName}</span>
                          <span style={{ color: getStatusColor(effectiveStatus) }}>{getStatusLabel(effectiveStatus)}</span>
                          <span>{new Date(job.createdAt).toLocaleString()}</span>
                        </div>
                        <div className="processing-job-progress">
                          <div className="progress-bar">
                            <div
                              className="progress-fill"
                              style={{ width: `${job.progress}%`, color: getStatusColor(effectiveStatus) }}
                            />
                          </div>
                          <div className="progress-text">{job.progress}%</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

      </main>
      {choosePlan ? <ChoosePlanModal /> : <ChoosePlanGate />}
    </div>
  );
};

export default Dashboard;
